import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, CATEGORY_LABELS, SIZE_LABELS, STATUS_LABELS, STATUS_COLORS } from "@/lib/constants";
import { MapPin, Calendar, Package, Search, Map as MapIcon, Grid3x3, Locate, X, Loader2, Leaf } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Default Leaflet marker fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

export default function BrowseJobs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid");
  const [nearMe, setNearMe] = useState(null);       // {lat, lng} or null
  const [radiusKm, setRadiusKm] = useState(25);
  const [locating, setLocating] = useState(false);
  const [bikeOnly, setBikeOnly] = useState(false);
  const category = searchParams.get("category") || "all";

  useEffect(() => {
    setLoading(true);
    const params = { status: "open" };
    if (category !== "all") params.category = category;
    if (bikeOnly) params.bike_friendly = true;
    if (nearMe) { params.lat = nearMe.lat; params.lng = nearMe.lng; params.radius_km = radiusKm; }
    api.get("/jobs", { params })
      .then((r) => setJobs(r.data))
      .finally(() => setLoading(false));
  }, [category, nearMe, radiusKm, bikeOnly]);

  const findNearMe = () => {
    if (!navigator.geolocation) { toast.error("Geolokation ikke understøttet"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setNearMe({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
        toast.success("Position fundet, viser jobs nær dig");
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === 1 ? "Tilladelse afvist" : "Kunne ikke finde din position");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  const clearNearMe = () => setNearMe(null);

  const filtered = jobs.filter(j =>
    !search || j.title?.toLowerCase().includes(search.toLowerCase()) ||
    j.pickup_address?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Find jobs at byde på</h1>
        <p className="text-muted-foreground">{filtered.length} åbne jobs i Danmark</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Søg efter titel eller adresse..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="job-search-input"
          />
        </div>
        <Select value={category} onValueChange={(v) => setSearchParams(v === "all" ? {} : { category: v })}>
          <SelectTrigger className="w-full sm:w-56" data-testid="category-filter"><SelectValue placeholder="Alle kategorier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle kategorier</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          <Button size="sm" variant={view === "grid" ? "default" : "ghost"} onClick={() => setView("grid")} className="h-8" data-testid="view-grid"><Grid3x3 className="w-4 h-4" /></Button>
          <Button size="sm" variant={view === "map" ? "default" : "ghost"} onClick={() => setView("map")} className="h-8" data-testid="view-map"><MapIcon className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Near me row */}
      <div className="flex flex-wrap gap-2 items-center mb-6">
        <button
          onClick={() => setBikeOnly(!bikeOnly)}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${bikeOnly ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-background text-muted-foreground border-border hover:border-emerald-300 hover:text-emerald-700"}`}
          data-testid="bike-friendly-filter"
        >
          <Leaf className="w-3.5 h-3.5" /> Kan hentes på ladcykel
        </button>
        {!nearMe ? (
          <Button size="sm" variant="outline" onClick={findNearMe} disabled={locating} className="rounded-full gap-2" data-testid="near-me-btn">
            {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Locate className="w-3.5 h-3.5" />}
            Find jobs nær mig
          </Button>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-3 py-1.5 text-xs font-semibold">
              <Locate className="w-3.5 h-3.5" /> Nær mig (inden for {radiusKm} km)
              <button onClick={clearNearMe} className="hover:bg-primary/20 rounded-full w-4 h-4 flex items-center justify-center" data-testid="clear-near-me"><X className="w-3 h-3" /></button>
            </div>
            <Select value={String(radiusKm)} onValueChange={(v) => setRadiusKm(Number(v))}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="radius-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 km</SelectItem>
                <SelectItem value="10">10 km</SelectItem>
                <SelectItem value="25">25 km</SelectItem>
                <SelectItem value="50">50 km</SelectItem>
                <SelectItem value="100">100 km</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Card key={i} className="h-64 animate-pulse bg-muted/30" />)}
        </div>
      ) : view === "map" ? (
        <Card className="h-[600px] overflow-hidden">
          <MapContainer center={[55.9, 11.5]} zoom={7} className="h-full w-full">
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
            {filtered.filter(j => j.pickup_lat && j.pickup_lng).map(j => (
              <Marker key={j.id} position={[j.pickup_lat, j.pickup_lng]}>
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold">{j.title}</p>
                    <p className="text-xs">{j.budget} DKK</p>
                    <Link to={`/jobs/${j.id}`} className="text-primary text-xs">Se job →</Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">Ingen jobs matcher dine filtre lige nu.</p>
          <Link to="/post"><Button className="mt-4">Opret det første</Button></Link>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((j) => <JobCard key={j.id} job={j} />)}
        </div>
      )}
    </div>
  );
}

function JobCard({ job }) {
  return (
    <Link to={`/jobs/${job.id}`} data-testid={`job-card-${job.id}`}>
      <Card className="p-5 h-full lift cursor-pointer relative overflow-hidden group">
        <div className="absolute -top-12 -right-12 w-28 h-28 bg-primary/5 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="relative">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge variant="secondary" className="text-xs">{CATEGORY_LABELS[job.category]}</Badge>
              {job.bike_friendly && <Badge className="text-[11px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1" data-testid="job-bike-badge"><Leaf className="w-3 h-3" /> Ladcykel OK</Badge>}
            </div>
            <Badge variant="outline" className={`${STATUS_COLORS[job.status]} text-xs`}>{STATUS_LABELS[job.status]}</Badge>
          </div>
          <h3 className="font-semibold text-base mb-2 line-clamp-2 leading-tight group-hover:text-primary transition-colors">{job.title}</h3>
          {job.distance_km !== undefined && (
            <p className="text-xs text-primary font-semibold mb-2 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {job.distance_km} km væk
            </p>
          )}
          {job.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{job.description}</p>}
          <div className="space-y-1.5 text-xs text-muted-foreground mb-4">
            <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{job.pickup_address}</span></div>
            {job.preferred_date && <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 shrink-0" /> {job.preferred_date}</div>}
            {job.size_estimate && <div className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5 shrink-0" /> {SIZE_LABELS[job.size_estimate]}</div>}
          </div>
          <div className="flex items-end justify-between pt-3 border-t border-border">
            <span className="text-xs text-muted-foreground">Budget</span>
            <span className="text-xl font-bold text-primary">{job.budget} DKK</span>
          </div>
        </div>
      </Card>
    </Link>
  );
}
